import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  getOwnerFlag,
  killOwnerFlag,
  listOwnerCohorts,
  listOwnerFlagAdoption,
  listOwnerFlagHistory,
  listOwnerFlags,
  restoreOwnerFlag,
  rollbackOwnerFlag,
  updateOwnerFlag,
  type OwnerFlagAdoptionDaySummary,
  type OwnerFlagDetail,
  type OwnerFlagHistoryEntry,
  type OwnerFlagRule,
} from '../../../services/ownerConsoleService'
import { useNls } from '../../../nls/nlsProvider'
import { useRoles } from '../context/RolesContext'
import { isSessionStaleError, useSessionLease } from '../context/SessionLeaseProvider'
import { OwnerTable, type ColumnDef } from '../kit/OwnerTable'
import { OwnerFirmConfirm, OwnerHardConfirm } from '../kit/OwnerConfirm'
import { OwnerEmptyScaffold } from '../kit/OwnerEmptyScaffold'
import { OwnerLineChart } from '../kit/OwnerLineChart'
import { navigateOwner, type OwnerRoute } from '../ownerRouter'
import {
  RAMP_PRESETS,
  describeFlagHistoryChange,
  type FlagHistoryChangeParts,
  FLAG_HISTORY_ACTION_NLS_KEYS,
  extractErrorMessage,
  getFlagMutationErrorKeyAndParams,
} from './ownerFlagsModel'
import { OwnerFlagImpactPreview } from './OwnerFlagImpactPreview'
import './ownerFlags.css'

interface CohortLookup {
  id: string
  name: string
  archived: boolean
}

function isRuleValid(rule: OwnerFlagRule): boolean {
  if (!rule || !rule.cohortId || rule.cohortId.trim().length === 0) return false
  const pct = Number(rule.percentage)
  if (!Number.isInteger(pct) || pct < 0 || pct > 100) return false
  return typeof rule.value === 'boolean'
}

function formatUpdatedAt(val: unknown, neverText: string): string {
  if (!val) return neverText
  let ms: number | null = null
  if (typeof val === 'number') {
    ms = val
  } else if (typeof val === 'string') {
    const parsed = Date.parse(val)
    if (!Number.isNaN(parsed)) ms = parsed
  } else if (typeof val === 'object' && val !== null && '_seconds' in val) {
    const sec = Number((val as { _seconds: unknown })._seconds)
    if (!Number.isNaN(sec)) ms = sec * 1000
  }
  if (!ms) return neverText
  return new Date(ms).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function OwnerFlagsPanel({ route }: { route: OwnerRoute }) {
  const { t } = useNls()
  const { can } = useRoles()
  const canEdit = can('featureFlags:flip')
  const { triggerSessionStale } = useSessionLease()

  const isDetail = route.view === 'config-flag-detail'
  const activeFlagKey = route.view === 'config-flag-detail' ? route.flagKey : null

  // List view state
  const [flags, setFlags] = useState<OwnerFlagDetail[]>([])
  const [isListLoading, setIsListLoading] = useState(false)
  const [listErrorMessage, setListErrorMessage] = useState('')

  // Shared cohorts state for lookup and selection
  const [allCohorts, setAllCohorts] = useState<CohortLookup[]>([])
  const [isCohortsLoading, setIsCohortsLoading] = useState(true)
  const [cohortErrorMessage, setCohortErrorMessage] = useState('')

  // Detail view state
  const [detailFlag, setDetailFlag] = useState<OwnerFlagDetail | null>(null)
  const [isDetailLoading, setIsDetailLoading] = useState(false)
  const [detailErrorMessage, setDetailErrorMessage] = useState('')
  const [description, setDescription] = useState('')
  const [isDescriptionDirty, setIsDescriptionDirty] = useState(false)
  const [defaultValue, setDefaultValue] = useState(false)
  const [rules, setRules] = useState<OwnerFlagRule[]>([])
  const [dependsOn, setDependsOn] = useState<string[]>([])
  const [allFlagKeys, setAllFlagKeys] = useState<string[]>([])

  // Modal states
  const [isConfirmOpen, setIsConfirmOpen] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isKillConfirmOpen, setIsKillConfirmOpen] = useState(false)
  const [isKillSaving, setIsKillSaving] = useState(false)
  const [isRestoreConfirmOpen, setIsRestoreConfirmOpen] = useState(false)
  const [isRestoreSaving, setIsRestoreSaving] = useState(false)
  const [rollbackTargetVersion, setRollbackTargetVersion] = useState<number | null>(null)
  const [isRollbackSaving, setIsRollbackSaving] = useState(false)

  // Feedback banner
  const [saveSuccessMessage, setSaveSuccessMessage] = useState('')

  // History state
  const [historyEntries, setHistoryEntries] = useState<OwnerFlagHistoryEntry[]>([])
  const [historyNextBeforeVersion, setHistoryNextBeforeVersion] = useState<number | null>(null)
  const [isHistoryLoading, setIsHistoryLoading] = useState(false)

  // Adoption state — detail view (30 days)
  const [adoptionDays, setAdoptionDays] = useState<OwnerFlagAdoptionDaySummary[]>([])
  const [isAdoptionLoading, setIsAdoptionLoading] = useState(false)
  const [adoptionError, setAdoptionError] = useState('')

  // Adoption state — list view (1 day snapshot)
  const [listAdoptionDay, setListAdoptionDay] = useState<OwnerFlagAdoptionDaySummary | null>(null)
  const [listAdoptionCapped, setListAdoptionCapped] = useState(false)

  const cohortsById = useMemo(() => {
    const map = new Map<string, CohortLookup>()
    for (const c of allCohorts) {
      map.set(c.id, c)
    }
    return map
  }, [allCohorts])

  const activeCohorts = useMemo(() => allCohorts.filter((c) => !c.archived), [allCohorts])

  const loadHistory = useCallback(async (key: string) => {
    setIsHistoryLoading(true)
    try {
      const res = await listOwnerFlagHistory({ key, limit: 25 })
      setHistoryEntries(res.entries || [])
      setHistoryNextBeforeVersion(res.nextBeforeVersion)
    } catch {
      // Non-fatal
    } finally {
      setIsHistoryLoading(false)
    }
  }, [])

  const loadAdoption = useCallback(async (key: string) => {
    setIsAdoptionLoading(true)
    setAdoptionError('')
    try {
      const res = await listOwnerFlagAdoption({ key, days: 30 })
      setAdoptionDays(res.days || [])
    } catch {
      setAdoptionError('NLSOwnerConsoleFlagsAdoptionError')
    } finally {
      setIsAdoptionLoading(false)
    }
  }, [])

  const handleLoadMoreHistory = async () => {
    if (!detailFlag || historyNextBeforeVersion === null || isHistoryLoading) return
    setIsHistoryLoading(true)
    try {
      const res = await listOwnerFlagHistory({
        key: detailFlag.key,
        beforeVersion: historyNextBeforeVersion,
        limit: 25,
      })
      setHistoryEntries((prev) => [...prev, ...(res.entries || [])])
      setHistoryNextBeforeVersion(res.nextBeforeVersion)
    } catch {
      // Non-fatal
    } finally {
      setIsHistoryLoading(false)
    }
  }

  type FlagSuccessKey =
    | 'NLSOwnerConsoleFlagsSaveSuccess'
    | 'NLSOwnerConsoleFlagsKillSuccess'
    | 'NLSOwnerConsoleFlagsRestoreSuccess'
    | 'NLSOwnerConsoleFlagsHistoryRollbackSuccess'

  const applyUpdatedFlag = useCallback(
    (flag: OwnerFlagDetail, successKey: FlagSuccessKey) => {
      setDetailFlag(flag)
      setDescription(flag.description || '')
      setIsDescriptionDirty(false)
      setDefaultValue(Boolean(flag.defaultValue))
      setRules(flag.rules || [])
      setDependsOn(flag.dependsOn || [])
      setSaveSuccessMessage(t(successKey))
      void loadHistory(flag.key)
      void loadAdoption(flag.key)
    },
    [loadHistory, loadAdoption, t],
  )

  // Load cohorts for name resolution and rule builder
  useEffect(() => {
    let active = true
    const run = async () => {
      setIsCohortsLoading(true)
      try {
        const res = await listOwnerCohorts({ includeArchived: true })
        if (active) {
          const lookup: CohortLookup[] = (res.cohorts || []).map((c) => ({
            id: c.id,
            name: c.name,
            archived: Boolean(c.archived),
          }))
          setAllCohorts(lookup)
          setIsCohortsLoading(false)
        }
      } catch (err) {
        if (!active) return
        setIsCohortsLoading(false)
        if (isSessionStaleError(err)) {
          triggerSessionStale()
        } else {
          setCohortErrorMessage(t('NLSOwnerConsoleFlagsErrorLoadCohorts'))
        }
      }
    }
    void run()
    return () => {
      active = false
    }
  }, [triggerSessionStale, t])

  // Load flags list or detail
  useEffect(() => {
    let active = true
    if (isDetail && activeFlagKey) {
      const run = async () => {
        setIsDetailLoading(true)
        setDetailErrorMessage('')
        try {
          const [flagRes, flagsRes] = await Promise.all([
            getOwnerFlag({ key: activeFlagKey }),
            listOwnerFlags().catch(() => null),
          ])
          if (!active) return
          const flag = flagRes.flag
          setDetailFlag(flag)
          setDescription(flag.description || '')
          setIsDescriptionDirty(false)
          setDefaultValue(Boolean(flag.defaultValue))
          setRules(flag.rules || [])
          setDependsOn(flag.dependsOn || [])
          if (flagsRes?.flags) {
            setAllFlagKeys(flagsRes.flags.map((f) => f.key))
          }
          setIsDetailLoading(false)
          void loadHistory(activeFlagKey)
          void loadAdoption(activeFlagKey)
        } catch (err) {
          if (!active) return
          if (isSessionStaleError(err)) {
            triggerSessionStale()
          }
          setDetailErrorMessage(t('NLSOwnerConsoleFlagsErrorLoadDetail'))
          setIsDetailLoading(false)
        }
      }
      void run()
    } else if (!isDetail) {
      const run = async () => {
        setIsListLoading(true)
        setListErrorMessage('')
        try {
          const [flagsRes, adoptionRes] = await Promise.all([
            listOwnerFlags(),
            listOwnerFlagAdoption({ days: 1 }).catch(() => null),
          ])
          if (!active) return
          setFlags(flagsRes.flags || [])
          setAllFlagKeys((flagsRes.flags || []).map((f) => f.key))
          const latestDay = adoptionRes?.days?.[0] ?? null
          setListAdoptionDay(latestDay)
          setListAdoptionCapped(Boolean(latestDay?.capped))
          setIsListLoading(false)
        } catch (err) {
          if (!active) return
          if (isSessionStaleError(err)) {
            triggerSessionStale()
          }
          setListErrorMessage(t('NLSOwnerConsoleFlagsErrorLoadList'))
          setIsListLoading(false)
        }
      }
      void run()
    }
    return () => {
      active = false
    }
  }, [isDetail, activeFlagKey, triggerSessionStale, loadHistory, loadAdoption, t])

  // Status formatter in plain words
  const formatFlagStatus = useCallback(
    (flag: OwnerFlagDetail): string => {
      const defaultOnStr = t('NLSOwnerConsoleFlagsOn')
      const defaultOffStr = t('NLSOwnerConsoleFlagsOff')
      const flagRules = flag.rules || []

      if (flagRules.length === 0) {
        return flag.defaultValue
          ? t('NLSOwnerConsoleFlagsStatusDefaultOn')
          : t('NLSOwnerConsoleFlagsStatusDefaultOff')
      }

      const ruleDescriptions = flagRules.map((rule) => {
        const onOff = rule.value ? defaultOnStr : defaultOffStr
        const cohort = cohortsById.get(rule.cohortId)
        const cohortName = cohort ? cohort.name : rule.cohortId
        if (rule.percentage === 100) {
          return t('NLSOwnerConsoleFlagsStatusRuleFull', { value: onOff, cohort: cohortName })
        }
        return t('NLSOwnerConsoleFlagsStatusRulePercent', {
          value: onOff,
          percent: String(rule.percentage),
          cohort: cohortName,
        })
      })

      const rulesStr = ruleDescriptions.join(', ')
      const defaultStr = flag.defaultValue ? defaultOnStr : defaultOffStr
      return t('NLSOwnerConsoleFlagsStatusThenDefault', {
        rules: rulesStr,
        default: defaultStr,
      })
    },
    [cohortsById, t],
  )

  // Rule operations
  const handleAddRule = () => {
    setRules((prev) => [...prev, { cohortId: '', value: true, percentage: 100 }])
  }

  const handleRemoveRule = (index: number) => {
    setRules((prev) => prev.filter((_, idx) => idx !== index))
  }

  const handleMoveRule = (index: number, direction: 'up' | 'down') => {
    setRules((prev) => {
      const target = direction === 'up' ? index - 1 : index + 1
      if (target < 0 || target >= prev.length) return prev
      const updated = [...prev]
      const [moved] = updated.splice(index, 1)
      updated.splice(target, 0, moved)
      return updated
    })
  }

  const handleRuleChange = (
    index: number,
    field: keyof OwnerFlagRule,
    val: string | boolean | number,
  ) => {
    setRules((prev) => {
      const updated = [...prev]
      const existing = updated[index]
      if (!existing) return prev
      if (field === 'cohortId') {
        updated[index] = { ...existing, cohortId: String(val) }
      } else if (field === 'value') {
        updated[index] = { ...existing, value: Boolean(val) }
      } else if (field === 'percentage') {
        const num = val === '' ? NaN : Number(val)
        updated[index] = { ...existing, percentage: num }
      }
      return updated
    })
  }

  const handleToggleDependency = (depKey: string) => {
    setDependsOn((prev) => {
      if (prev.includes(depKey)) {
        return prev.filter((k) => k !== depKey)
      }
      if (prev.length >= 5) return prev
      return [...prev, depKey]
    })
  }

  // Save handler
  const handleSave = async (reason: string) => {
    if (!detailFlag) return
    setIsSaving(true)
    setDetailErrorMessage('')
    setSaveSuccessMessage('')
    try {
      const payload = {
        key: detailFlag.key,
        defaultValue,
        rules: rules.map((r) => ({
          cohortId: r.cohortId,
          value: r.value,
          percentage: Number(r.percentage),
        })),
        dependsOn,
        reason,
        description: isDescriptionDirty && description.trim() ? description.trim() : undefined,
      }
      const res = await updateOwnerFlag(payload)
      setIsConfirmOpen(false)
      applyUpdatedFlag(res.flag, 'NLSOwnerConsoleFlagsSaveSuccess')
    } catch (err: unknown) {
      if (isSessionStaleError(err)) {
        triggerSessionStale()
      }
      const msg = extractErrorMessage(err)
      if (msg.includes('Dependency cycle:')) {
        const cycleIdx = msg.indexOf('Dependency cycle:')
        const path = msg.slice(cycleIdx + 'Dependency cycle:'.length).trim()
        setDetailErrorMessage(t('NLSOwnerConsoleFlagsErrorCycle', { path }))
      } else {
        const { key, params } = getFlagMutationErrorKeyAndParams(err)
        setDetailErrorMessage(t(key, params))
      }
    } finally {
      setIsSaving(false)
    }
  }

  // Kill switch handler
  const handleKill = async (reason: string) => {
    if (!detailFlag) return
    setIsKillSaving(true)
    setDetailErrorMessage('')
    setSaveSuccessMessage('')
    try {
      const res = await killOwnerFlag({
        key: detailFlag.key,
        confirmKey: detailFlag.key,
        reason,
      })
      setIsKillConfirmOpen(false)
      applyUpdatedFlag(res.flag, 'NLSOwnerConsoleFlagsKillSuccess')
    } catch (err: unknown) {
      if (isSessionStaleError(err)) {
        triggerSessionStale()
      }
      const { key, params } = getFlagMutationErrorKeyAndParams(err)
      setDetailErrorMessage(t(key, params))
    } finally {
      setIsKillSaving(false)
    }
  }

  // Restore handler
  const handleRestore = async (reason: string) => {
    if (!detailFlag) return
    setIsRestoreSaving(true)
    setDetailErrorMessage('')
    setSaveSuccessMessage('')
    try {
      const res = await restoreOwnerFlag({
        key: detailFlag.key,
        confirmKey: detailFlag.key,
        reason,
      })
      setIsRestoreConfirmOpen(false)
      applyUpdatedFlag(res.flag, 'NLSOwnerConsoleFlagsRestoreSuccess')
    } catch (err: unknown) {
      if (isSessionStaleError(err)) {
        triggerSessionStale()
      }
      const { key, params } = getFlagMutationErrorKeyAndParams(err)
      setDetailErrorMessage(t(key, params))
    } finally {
      setIsRestoreSaving(false)
    }
  }

  // Rollback handler
  const handleRollback = async (version: number, reason: string) => {
    if (!detailFlag) return
    setIsRollbackSaving(true)
    setDetailErrorMessage('')
    setSaveSuccessMessage('')
    try {
      const res = await rollbackOwnerFlag({
        key: detailFlag.key,
        version,
        reason,
      })
      setRollbackTargetVersion(null)
      applyUpdatedFlag(res.flag, 'NLSOwnerConsoleFlagsHistoryRollbackSuccess')
    } catch (err: unknown) {
      if (isSessionStaleError(err)) {
        triggerSessionStale()
      }
      const { key, params } = getFlagMutationErrorKeyAndParams(err)
      setDetailErrorMessage(t(key, params))
    } finally {
      setIsRollbackSaving(false)
    }
  }

  const hasInvalidRules = useMemo(
    () =>
      isCohortsLoading ||
      rules.some((r) => {
        if (!isRuleValid(r)) return true
        const cohortObj = cohortsById.get(r.cohortId)
        return !cohortObj || cohortObj.archived
      }),
    [isCohortsLoading, rules, cohortsById],
  )
  const isKilled = Boolean(detailFlag?.killed)
  const isSaveDisabled = !canEdit || isKilled || isSaving || isCohortsLoading || hasInvalidRules

  const availableFlagKeys = useMemo(
    () => allFlagKeys.filter((k) => k !== detailFlag?.key),
    [allFlagKeys, detailFlag?.key],
  )

  const renderHistoryDiff = (parts: FlagHistoryChangeParts) => {
    const elements: React.ReactNode[] = []
    if (parts.killed) {
      elements.push(<span key="killed">{t('NLSOwnerConsoleFlagsHistoryKilled')}</span>)
    }
    if (parts.restored) {
      elements.push(<span key="restored">{t('NLSOwnerConsoleFlagsHistoryRestored')}</span>)
    }
    if (parts.rolledBackFrom != null) {
      elements.push(
        <span key="rollback">
          {t('NLSOwnerConsoleFlagsHistoryRolledBack', { version: parts.rolledBackFrom })}
        </span>,
      )
    }
    if (parts.defaultChanged) {
      elements.push(
        <span key="default">
          {t('NLSOwnerConsoleFlagsHistoryDefaultChanged', {
            from: parts.defaultChanged.from
              ? t('NLSOwnerConsoleFlagsOn')
              : t('NLSOwnerConsoleFlagsOff'),
            to: parts.defaultChanged.to
              ? t('NLSOwnerConsoleFlagsOn')
              : t('NLSOwnerConsoleFlagsOff'),
          })}
        </span>,
      )
    }
    if (parts.rulesCountChanged) {
      elements.push(
        <span key="rules">
          {t('NLSOwnerConsoleFlagsHistoryRulesChanged', {
            from: parts.rulesCountChanged.from,
            to: parts.rulesCountChanged.to,
          })}
        </span>,
      )
    }
    if (parts.dependenciesAdded.length > 0) {
      elements.push(
        <span key="deps-add">
          {t('NLSOwnerConsoleFlagsHistoryDepsAdded', {
            keys: parts.dependenciesAdded.join(', '),
          })}
        </span>,
      )
    }
    if (parts.dependenciesRemoved.length > 0) {
      elements.push(
        <span key="deps-rem">
          {t('NLSOwnerConsoleFlagsHistoryDepsRemoved', {
            keys: parts.dependenciesRemoved.join(', '),
          })}
        </span>,
      )
    }
    if (elements.length === 0) {
      return <span>{t('NLSOwnerConsoleFlagsHistoryNoChanges')}</span>
    }
    return elements.map((elem, i) => (
      <span key={i}>
        {i > 0 && ' | '}
        {elem}
      </span>
    ))
  }

  // Adoption percentage for a flag key from the list-view 1-day snapshot
  const getLatestAdoptionPct = useCallback(
    (flagKey: string): string => {
      if (!listAdoptionDay) return '—'
      const stat = listAdoptionDay.flags[flagKey]
      if (!stat) return '—'
      return `${stat.pct.toFixed(1)}%`
    },
    [listAdoptionDay],
  )

  // Table columns for list view
  const columns: ColumnDef<OwnerFlagDetail>[] = useMemo(
    () => [
      {
        key: 'key',
        header: t('NLSOwnerConsoleFlagsColKey'),
        render: (row) => <span className="ownerFlagsKeyText">{row.key}</span>,
      },
      {
        key: 'description',
        header: t('NLSOwnerConsoleFlagsColDescription'),
        render: (row) => <span className="ownerFlagsDescText">{row.description}</span>,
      },
      {
        key: 'default',
        header: t('NLSOwnerConsoleFlagsColDefault'),
        render: (row) => (
          <span className="ownerFlagsDefaultText">
            {row.defaultValue ? t('NLSOwnerConsoleFlagsOn') : t('NLSOwnerConsoleFlagsOff')}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('NLSOwnerConsoleFlagsColStatus'),
        render: (row) => (
          <div className="ownerFlagsStatusCell">
            <span>{formatFlagStatus(row)}</span>
            {row.killed && (
              <span className="ownerFlagsChip ownerFlagsChipKilled">
                {t('NLSOwnerConsoleFlagsKilledChip')}
              </span>
            )}
            {!row.enforced && (
              <span className="ownerFlagsChip ownerFlagsChipNotEnforced">
                {t('NLSOwnerConsoleFlagsNotEnforcedChip')}
              </span>
            )}
            {row.dependsOn && row.dependsOn.length > 0 && (
              <span className="ownerFlagsChip ownerFlagsChipNeeds">
                {t('NLSOwnerConsoleFlagsNeedsChip', { deps: row.dependsOn.join(', ') })}
              </span>
            )}
          </div>
        ),
      },
      {
        key: 'adoption',
        header: t('NLSOwnerConsoleFlagsColAdoption'),
        render: (row) => (
          <span className="ownerFlagsAdoptionText">
            {getLatestAdoptionPct(row.key)}
          </span>
        ),
      },
      {
        key: 'updatedAt',
        header: t('NLSOwnerConsoleFlagsColUpdated'),
        render: (row) => (
          <span className="ownerFlagsUpdatedText">
            {formatUpdatedAt(row.updatedAt, t('NLSOwnerConsoleFlagsNever'))}
          </span>
        ),
      },
    ],
    [formatFlagStatus, getLatestAdoptionPct, t],
  )

  // Detail view rendering
  if (isDetail) {
    return (
      <div className="ownerFlagsPage">
        <div className="ownerFlagsHeader">
          <div>
            <button
              type="button"
              className="ownerFlagsButton"
              onClick={() => navigateOwner({ view: 'config-flags' })}
            >
              {t('NLSOwnerConsoleFlagsBack')}
            </button>
            <h1 className="ownerFlagsDetailTitle">
              {t('NLSOwnerConsoleFlagsDetailTitle', { key: activeFlagKey || '' })}
            </h1>
          </div>
          <div className="ownerFlagsHeaderActions">
            {canEdit && detailFlag && !detailFlag.killed && (
              <button
                type="button"
                className="ownerFlagsButton ownerFlagsButtonDanger"
                onClick={() => setIsKillConfirmOpen(true)}
              >
                {t('NLSOwnerConsoleFlagsKillButton')}
              </button>
            )}
            {canEdit && detailFlag && detailFlag.killed && (
              <button
                type="button"
                className="ownerFlagsButton"
                onClick={() => setIsRestoreConfirmOpen(true)}
              >
                {t('NLSOwnerConsoleFlagsRestoreButton')}
              </button>
            )}
            {canEdit && (
              <button
                type="button"
                className="ownerFlagsButton ownerFlagsButtonPrimary"
                disabled={isSaveDisabled}
                onClick={() => setIsConfirmOpen(true)}
              >
                {isSaving ? t('NLSOwnerConsoleFlagsSaving') : t('NLSOwnerConsoleFlagsSave')}
              </button>
            )}
          </div>
        </div>

        {!canEdit && (
          <div className="ownerFlagsNotice">{t('NLSOwnerConsoleFlagsReadOnlyNotice')}</div>
        )}

        {detailFlag && isKilled && (
          <div className="ownerFlagsKilledBanner">
            <div className="ownerFlagsKilledBannerTitle">
              {t('NLSOwnerConsoleFlagsKilledBanner')}
            </div>
            <div className="ownerFlagsKilledBannerMeta">
              {t('NLSOwnerConsoleFlagsKilledMeta', {
                by: detailFlag.killedBy || t('NLSOwnerConsoleFlagsUnknown'),
                at: formatUpdatedAt(detailFlag.killedAt, t('NLSOwnerConsoleFlagsNever')),
                reason: detailFlag.killReason || '',
              })}
            </div>
          </div>
        )}

        {detailFlag && !detailFlag.enforced && (
          <div className="ownerFlagsNotice">{t('NLSOwnerConsoleFlagsNotEnforcedBanner')}</div>
        )}

        {cohortErrorMessage && <div className="ownerFlagsAlert">{cohortErrorMessage}</div>}
        {detailErrorMessage && <div className="ownerFlagsAlert">{detailErrorMessage}</div>}
        {saveSuccessMessage && (
          <div className="ownerFlagsSuccessAlert">{saveSuccessMessage}</div>
        )}

        {isDetailLoading && (
          <div className="ownerFlagsEditorCard">
            <p className="ownerFlagsLoadingText">{t('NLSOwnerConsoleLoadingData')}</p>
          </div>
        )}

        {!isDetailLoading && detailFlag && (
          <div className={`ownerFlagsEditorCard ${isKilled ? 'ownerFlagsEditorDisabled' : ''}`}>
            <div className="ownerFlagsFieldGroup">
              <label htmlFor="owner-flag-key">{t('NLSOwnerConsoleFlagsKeyLabel')}</label>
              <input id="owner-flag-key" type="text" value={detailFlag.key} readOnly />
            </div>

            <div className="ownerFlagsFieldGroup">
              <label htmlFor="owner-flag-desc">{t('NLSOwnerConsoleFlagsDescLabel')}</label>
              <input
                id="owner-flag-desc"
                type="text"
                placeholder={t('NLSOwnerConsoleFlagsDescPlaceholder')}
                value={description}
                disabled={!canEdit || isKilled}
                onChange={(e) => {
                  setDescription(e.target.value)
                  setIsDescriptionDirty(true)
                }}
              />
            </div>

            <div className="ownerFlagsFieldGroup">
              <label>{t('NLSOwnerConsoleFlagsDefaultLabel')}</label>
              <div className="ownerFlagsToggleRow">
                <button
                  type="button"
                  className={`ownerFlagsToggleButton ${defaultValue ? 'ownerFlagsToggleButtonActive' : ''}`}
                  disabled={!canEdit || isKilled}
                  onClick={() => setDefaultValue(true)}
                >
                  {t('NLSOwnerConsoleFlagsOn')}
                </button>
                <button
                  type="button"
                  className={`ownerFlagsToggleButton ${!defaultValue ? 'ownerFlagsToggleButtonActive' : ''}`}
                  disabled={!canEdit || isKilled}
                  onClick={() => setDefaultValue(false)}
                >
                  {t('NLSOwnerConsoleFlagsOff')}
                </button>
              </div>
            </div>

            <div className="ownerFlagsNeedsSection">
              <label>{t('NLSOwnerConsoleFlagsNeedsLabel')}</label>
              <p className="ownerFlagsTargetingSubtext">
                {t('NLSOwnerConsoleFlagsNeedsHelp')}
              </p>
              {dependsOn.length > 0 && (
                <div>
                  <span className="ownerFlagsChip ownerFlagsChipNeeds">
                    {t('NLSOwnerConsoleFlagsNeedsChip', { deps: dependsOn.join(', ') })}
                  </span>
                </div>
              )}
              <div className="ownerFlagsNeedsGrid">
                {availableFlagKeys.map((k) => {
                  const checked = dependsOn.includes(k)
                  const disabled =
                    !canEdit || isKilled || (!checked && dependsOn.length >= 5)
                  return (
                    <label key={k} className="ownerFlagsNeedsCheckboxLabel">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={disabled}
                        onChange={() => handleToggleDependency(k)}
                      />
                      <span>{k}</span>
                    </label>
                  )
                })}
              </div>
            </div>

            <div className="ownerFlagsRulesSection">
              <div className="ownerFlagsRulesHeader">
                <div>
                  <h3>{t('NLSOwnerConsoleFlagsTargetingHeading')}</h3>
                  <p className="ownerFlagsTargetingSubtext">
                    {t('NLSOwnerConsoleFlagsTargetingHelp')}
                  </p>
                </div>
                {canEdit && !isKilled && (
                  <button
                    type="button"
                    className="ownerFlagsButton"
                    onClick={handleAddRule}
                  >
                    {t('NLSOwnerConsoleFlagsAddRule')}
                  </button>
                )}
              </div>

              {rules.length === 0 ? (
                <p className="ownerFlagsNoRulesText">{t('NLSOwnerConsoleFlagsNoRules')}</p>
              ) : (
                <div className="ownerFlagsRulesList">
                  {rules.map((rule, idx) => {
                    const cohortObj = cohortsById.get(rule.cohortId)
                    const isMissingOrArchived =
                      !isCohortsLoading && (!cohortObj || cohortObj.archived)
                    return (
                      <div key={idx} className="ownerFlagsRuleRow">
                        <div className="ownerFlagsRuleInputs">
                          <div className="ownerFlagsFieldGroup">
                            <label>{t('NLSOwnerConsoleFlagsRuleCohortLabel')}</label>
                            <select
                              value={rule.cohortId}
                              disabled={!canEdit || isKilled}
                              onChange={(e) => handleRuleChange(idx, 'cohortId', e.target.value)}
                            >
                              <option value="" disabled>
                                {t('NLSOwnerConsoleFlagsRuleCohortSelect')}
                              </option>
                              {activeCohorts.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                              {isMissingOrArchived && rule.cohortId && (
                                <option value={rule.cohortId}>
                                  {cohortObj ? cohortObj.name : rule.cohortId}
                                </option>
                              )}
                            </select>
                          </div>

                          <div className="ownerFlagsFieldGroup">
                            <label>{t('NLSOwnerConsoleFlagsRuleValueLabel')}</label>
                            <select
                              value={rule.value ? 'true' : 'false'}
                              disabled={!canEdit || isKilled}
                              onChange={(e) =>
                                handleRuleChange(idx, 'value', e.target.value === 'true')
                              }
                            >
                              <option value="true">{t('NLSOwnerConsoleFlagsOn')}</option>
                              <option value="false">{t('NLSOwnerConsoleFlagsOff')}</option>
                            </select>
                          </div>

                          <div className="ownerFlagsFieldGroup ownerFlagsPercentageGroup">
                            <label>{t('NLSOwnerConsoleFlagsRulePercentageLabel')}</label>
                            <div className="ownerFlagsPresetRow">
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="1"
                                value={Number.isNaN(rule.percentage) ? '' : rule.percentage}
                                disabled={!canEdit || isKilled}
                                onChange={(e) =>
                                  handleRuleChange(idx, 'percentage', e.target.value)
                                }
                              />
                              <div className="ownerFlagsPresetGroup">
                                {RAMP_PRESETS.map((preset) => (
                                  <button
                                    key={preset}
                                    type="button"
                                    className="ownerFlagsPresetButton"
                                    disabled={!canEdit || isKilled}
                                    onClick={() => handleRuleChange(idx, 'percentage', preset)}
                                  >
                                    {preset}
                                  </button>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>

                        {canEdit && !isKilled && (
                          <div className="ownerFlagsRuleActions">
                            <button
                              type="button"
                              className="ownerFlagsButton"
                              disabled={idx === 0}
                              onClick={() => handleMoveRule(idx, 'up')}
                            >
                              {t('NLSOwnerConsoleFlagsMoveUp')}
                            </button>
                            <button
                              type="button"
                              className="ownerFlagsButton"
                              disabled={idx === rules.length - 1}
                              onClick={() => handleMoveRule(idx, 'down')}
                            >
                              {t('NLSOwnerConsoleFlagsMoveDown')}
                            </button>
                            <button
                              type="button"
                              className="ownerFlagsButton ownerFlagsButtonDanger"
                              onClick={() => handleRemoveRule(idx)}
                            >
                              {t('NLSOwnerConsoleFlagsRemoveRule')}
                            </button>
                          </div>
                        )}

                        {isMissingOrArchived && (
                          <div className="ownerFlagsRuleWarning">
                            {t('NLSOwnerConsoleFlagsRuleCohortMissingWarning')}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {!isDetailLoading && detailFlag && (
          <div className="ownerFlagsHistorySection">
            <div className="ownerFlagsRulesHeader">
              <div>
                <h3>{t('NLSOwnerConsoleFlagsAdoptionSectionHeading')}</h3>
                <p className="ownerFlagsTargetingSubtext">
                  {t('NLSOwnerConsoleFlagsAdoptionSectionHelp')}
                </p>
              </div>
            </div>

            {isAdoptionLoading ? (
              <p className="ownerFlagsLoadingText">{t('NLSOwnerConsoleLoadingData')}</p>
            ) : adoptionError ? (
              <p className="ownerFlagsAlert">{t('NLSOwnerConsoleFlagsAdoptionError')}</p>
            ) : (
              <OwnerLineChart
                points={adoptionDays.map((d) => ({
                  label: d.date,
                  value: adoptionDays.length > 0 && detailFlag
                    ? (d.flags[detailFlag.key]?.pct ?? 0)
                    : 0,
                }))}
                ariaLabel={t('NLSOwnerConsoleFlagsAdoptionSectionHeading')}
                emptyText={t('NLSOwnerConsoleFlagsAdoptionEmpty')}
              />
            )}
          </div>
        )}

        {!isDetailLoading && detailFlag && (
          <div className="ownerFlagsHistorySection">
            <div className="ownerFlagsRulesHeader">
              <div>
                <h3>{t('NLSOwnerConsoleFlagsHistoryHeading')}</h3>
                <p className="ownerFlagsTargetingSubtext">
                  {t('NLSOwnerConsoleFlagsHistoryHelp')}
                </p>
              </div>
            </div>

            {isHistoryLoading && historyEntries.length === 0 ? (
              <p className="ownerFlagsLoadingText">{t('NLSOwnerConsoleLoadingData')}</p>
            ) : historyEntries.length === 0 ? (
              <p className="ownerFlagsNoRulesText">{t('NLSOwnerConsoleFlagsHistoryNoEntries')}</p>
            ) : (
              <div className="ownerFlagsHistoryList">
                {historyEntries.map((entry, idx) => {
                  const olderEntry = historyEntries[idx + 1] || null
                  const parts = describeFlagHistoryChange(entry, olderEntry)
                  const isCurrent = entry.version === detailFlag.version

                  return (
                    <div key={entry.version} className="ownerFlagsHistoryItem">
                      <div className="ownerFlagsHistoryMeta">
                        <div className="ownerFlagsHistoryVersionBadge">
                          <span>
                            {t('NLSOwnerConsoleFlagsHistoryVersion', { version: entry.version })}
                          </span>
                          {isCurrent && (
                            <span className="ownerFlagsChip ownerFlagsChipNeeds">
                              {t('NLSOwnerConsoleFlagsHistoryCurrent')}
                            </span>
                          )}
                          {(() => {
                            const actionKey = FLAG_HISTORY_ACTION_NLS_KEYS[entry.action]
                            return <span>— {actionKey ? t(actionKey) : entry.action}</span>
                          })()}
                        </div>
                        <div className="ownerFlagsHistoryDetails">
                          <span>{entry.savedBy || t('NLSOwnerConsoleFlagsUnknown')}</span>
                          <span> • </span>
                          <span>{formatUpdatedAt(entry.savedAt, t('NLSOwnerConsoleFlagsNever'))}</span>
                          {entry.reason && (
                            <>
                              <span> • </span>
                              <span>{entry.reason}</span>
                            </>
                          )}
                        </div>
                        <div className="ownerFlagsHistoryDiff">{renderHistoryDiff(parts)}</div>
                      </div>

                      {canEdit && !isCurrent && (
                        <button
                          type="button"
                          className="ownerFlagsButton"
                          onClick={() => setRollbackTargetVersion(entry.version)}
                        >
                          {t('NLSOwnerConsoleFlagsHistoryRollbackBtn')}
                        </button>
                      )}
                    </div>
                  )
                })}

                {historyNextBeforeVersion !== null && (
                  <button
                    type="button"
                    className="ownerFlagsButton"
                    disabled={isHistoryLoading}
                    onClick={handleLoadMoreHistory}
                  >
                    {t('NLSOwnerConsoleFlagsHistoryLoadMore')}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {detailFlag && (
          <>
            <OwnerFirmConfirm
              isOpen={isConfirmOpen}
              title={t('NLSOwnerConsoleFlagsConfirmTitle')}
              description={
                <OwnerFlagImpactPreview
                  input={{
                    key: detailFlag.key,
                    change: 'update',
                    defaultValue,
                    rules,
                    dependsOn,
                  }}
                />
              }
              confirmLabel={t('NLSOwnerConsoleDoIt')}
              busy={isSaving}
              onConfirm={handleSave}
              onClose={() => setIsConfirmOpen(false)}
            />

            <OwnerHardConfirm
              isOpen={isKillConfirmOpen}
              title={t('NLSOwnerConsoleFlagsKillConfirmTitle')}
              targetHumanName={detailFlag.key}
              description={t('NLSOwnerConsoleFlagsKillConfirmDesc')}
              dryRunDiff={
                <OwnerFlagImpactPreview
                  input={{
                    key: detailFlag.key,
                    change: 'kill',
                  }}
                />
              }
              busy={isKillSaving}
              onConfirm={handleKill}
              onClose={() => setIsKillConfirmOpen(false)}
            />

            <OwnerHardConfirm
              isOpen={isRestoreConfirmOpen}
              title={t('NLSOwnerConsoleFlagsRestoreConfirmTitle')}
              targetHumanName={detailFlag.key}
              description={t('NLSOwnerConsoleFlagsRestoreConfirmDesc')}
              dryRunDiff={
                <OwnerFlagImpactPreview
                  input={{
                    key: detailFlag.key,
                    change: 'restore',
                  }}
                />
              }
              busy={isRestoreSaving}
              onConfirm={handleRestore}
              onClose={() => setIsRestoreConfirmOpen(false)}
            />

            <OwnerFirmConfirm
              isOpen={rollbackTargetVersion !== null}
              title={t('NLSOwnerConsoleFlagsHistoryRollbackTitle')}
              description={
                rollbackTargetVersion !== null ? (
                  <>
                    <span className="ownerFlagsConfirmPrompt">
                      {t('NLSOwnerConsoleFlagsHistoryRollbackDesc', {
                        version: rollbackTargetVersion,
                      })}
                    </span>
                    <OwnerFlagImpactPreview
                      input={{
                        key: detailFlag.key,
                        change: 'rollback',
                        version: rollbackTargetVersion,
                      }}
                    />
                  </>
                ) : undefined
              }
              confirmLabel={t('NLSOwnerConsoleFlagsHistoryRollbackButton')}
              busy={isRollbackSaving}
              onConfirm={async (reason) => {
                if (rollbackTargetVersion !== null) {
                  await handleRollback(rollbackTargetVersion, reason)
                }
              }}
              onClose={() => setRollbackTargetVersion(null)}
            />
          </>
        )}
      </div>
    )
  }

  // List view rendering
  return (
    <div className="ownerFlagsPage">
      <div className="ownerFlagsHeader">
        <div>
          <h1>{t('NLSOwnerConsoleFlagsTitle')}</h1>
          <p>
            <span>{t('NLSOwnerConsoleFlagsSubtitle')}</span>
            {' · '}
            <span className="ownerFlagsAdoptionHeaderSub">{t('NLSOwnerConsoleFlagsAdoptionTooltip')}</span>
            {listAdoptionCapped && (
              <>
                {' '}
                <span className="ownerFlagsChip">{t('NLSOwnerConsoleFlagsAdoptionCapped')}</span>
              </>
            )}
          </p>
        </div>
      </div>

      {cohortErrorMessage && <div className="ownerFlagsAlert">{cohortErrorMessage}</div>}
      {listErrorMessage && <div className="ownerFlagsAlert">{listErrorMessage}</div>}

      {isListLoading ? (
        <div className="ownerFlagsEditorCard">
          <p className="ownerFlagsLoadingText">{t('NLSOwnerConsoleLoadingData')}</p>
        </div>
      ) : flags.length === 0 ? (
        <OwnerEmptyScaffold
          title={t('NLSOwnerConsoleFlagsEmptyTitle')}
          body={t('NLSOwnerConsoleFlagsEmptySubtitle')}
        />
      ) : (
        <OwnerTable
          columns={columns}
          data={flags}
          viewKey="flags"
          getRowId={(row) => row.key}
          onRowClick={(row) => navigateOwner({ view: 'config-flag-detail', flagKey: row.key })}
        />
      )}
    </div>
  )
}
